import QRCode from 'qrcode';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  api, ApiError, deliveryApi, fmt, formatCustomFieldValuesWithLabels, readManagementToken, ticketLinkApi,
  type EventDto, type OrderDto, type PublicDeliveryStateDto,
} from '../api';
import DeliveryChannelPanel from '../components/DeliveryChannelPanel';
import { PreviewBanner } from '../components/PreviewBanner';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePreviewPassCapture } from '../hooks/usePreviewPass';
import { loadImage, roundRect } from '../lib/canvasCard';

/** JamTick's own support LINE OA — not the organizer's — matches server config LINE_ADD_FRIEND_URL. */
const JAMTICK_LINE_ADD_FRIEND_URL = 'https://line.me/R/ti/p/@568okcuh';

/**
 * TICK-096 — the organizer's own LINE OA, as page furniture rather than a step.
 *
 * It used to live inside `DeliveryChannelPanel`, behind `ticketReady`, so it reached only customers
 * on the v2 contract who had already verified a channel — and never a legacy order, an event with
 * the delivery rollout off, or a buyer reading this page on a second device. The reason it was
 * gated is still honoured, but by rank instead of by absence: this sits at the foot of the page,
 * styled below every transactional control, and says in its own words that ignoring it costs the
 * customer nothing. That way a customer still waiting on a ticket is never asked to choose between
 * this and the thing that actually gets them one.
 *
 * The organizer's message is rendered as text and never as markup — an organizer who could inject
 * markup here is an organizer who could make JamTick appear to say something about the order.
 */
function OrganizerOaInvite({ oa }: { oa: EventDto['organizerOa'] }) {
  if (!oa) return null;
  return (
    <section className="organizer-oa-invite" aria-label="ข่าวสารจากผู้จัดงาน" data-testid="organizer-oa-invite">
      <strong>รับข่าวสารจากผู้จัดงาน</strong>
      {oa.message && <span className="organizer-oa-invite-message">{oa.message}</span>}
      <a className="btn-ghost" href={oa.addFriendUrl} target="_blank" rel="noreferrer">
        เพิ่มเพื่อน LINE ผู้จัดงาน
      </a>
      <span className="organizer-oa-invite-note">
        ไม่บังคับ · ไม่เกี่ยวกับการรับบัตร — กดหรือไม่กด บัตรของคุณก็ส่งตามปกติ
      </span>
    </section>
  );
}

/** Composes the saved ticket image: dark bordered card, event header, QR, and a transaction-detail footer. */
async function buildTicketCardDataUrl(order: OrderDto, event: EventDto, qrDataUrl: string): Promise<string> {
  const [qrImg] = await Promise.all([loadImage(qrDataUrl), document.fonts.ready.catch(() => {})]);

  const scale = 2;
  const width = 420;
  const pad = 32;
  const qrBoxSize = 260;
  const lineLines = order.lines.map((l) => l.label);
  const headerH = 66;
  const qrH = qrBoxSize + 24;
  const codeH = 30;
  const footerH = 28 + lineLines.length * 20 + 30;
  const height = pad * 2 + headerH + qrH + codeH + footerH;

  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);

  const bg = '#171717';
  const border = '#3a3a3a';
  const fg = '#fafafa';
  const muted = '#a6a6a6';

  roundRect(ctx, 0.5, 0.5, width - 1, height - 1, 16);
  ctx.fillStyle = bg;
  ctx.fill();
  ctx.strokeStyle = border;
  ctx.lineWidth = 1;
  ctx.stroke();

  let y = pad;
  ctx.textAlign = 'center';
  ctx.fillStyle = fg;
  ctx.font = '700 17px Inter, sans-serif';
  ctx.fillText(event.name, width / 2, y + 16);
  y += 26;
  ctx.fillStyle = muted;
  ctx.font = '400 12px "JetBrains Mono", monospace';
  ctx.fillText(event.tagline, width / 2, y + 10);
  y += headerH - 26;

  // QR — white rounded tile, matching the on-page .qr-frame treatment.
  const qrBoxX = (width - qrBoxSize) / 2;
  roundRect(ctx, qrBoxX, y, qrBoxSize, qrBoxSize, 12);
  ctx.fillStyle = fg;
  ctx.fill();
  const qrInset = 20;
  ctx.drawImage(qrImg, qrBoxX + qrInset, y + qrInset, qrBoxSize - qrInset * 2, qrBoxSize - qrInset * 2);
  y += qrH;

  ctx.fillStyle = fg;
  ctx.font = '500 15px "JetBrains Mono", monospace';
  ctx.fillText(order.code, width / 2, y + 14);
  y += codeH;

  ctx.strokeStyle = border;
  ctx.beginPath();
  ctx.moveTo(pad, y);
  ctx.lineTo(width - pad, y);
  ctx.stroke();
  y += 22;

  ctx.font = '600 13px Inter, sans-serif';
  ctx.fillStyle = fg;
  ctx.fillText(`สแกนเข้างานได้ ${order.entries} คน`, width / 2, y);
  y += 22;

  ctx.font = '400 12.5px Inter, sans-serif';
  ctx.fillStyle = muted;
  for (const label of lineLines) {
    ctx.fillText(label, width / 2, y);
    y += 20;
  }
  y += 8;

  ctx.font = '600 13px Inter, sans-serif';
  ctx.fillStyle = fg;
  ctx.fillText(`ยอดชำระ ${fmt(order.total)}`, width / 2, y);

  return canvas.toDataURL('image/png');
}

export default function OrderStatus() {
  const { orderKey = '', eventSlug, token } = useParams<{ orderKey: string; eventSlug?: string; token?: string }>();
  const [linkedEventSlug, setLinkedEventSlug] = useState('');
  const effectiveEventSlug = eventSlug ?? linkedEventSlug;
  const prefix = effectiveEventSlug ? `/e/${effectiveEventSlug}` : '';
  const navigate = useNavigate();

  usePageTitle(effectiveEventSlug, 'Order Status');
  /*
   * TICK-085 — the page a UAT tester lands on after paying, and the one `orderStatusUrl` texts
   * them. TICK-081 captured the pass on `Landing` only, so a preview link pointing *here* dropped
   * its token and the gate answered 404 — which this page renders as "ไม่พบคำสั่งซื้อ", on the
   * worst possible screen. Read off the route's own slug rather than `effectiveEventSlug`, which
   * is still empty on the first render of a `/t/:token` arrival.
   */
  usePreviewPassCapture(eventSlug);
  const [order, setOrder] = useState<OrderDto | null>(null);
  const [event, setEvent] = useState<EventDto | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [savingCard, setSavingCard] = useState(false);

  const saveTicketCard = async () => {
    if (!order || !event || !qrDataUrl || savingCard) return;
    setSavingCard(true);
    try {
      const dataUrl = await buildTicketCardDataUrl(order, event, qrDataUrl);
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `jamtick-${order.code}.png`;
      a.click();
    } finally {
      setSavingCard(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  useEffect(() => {
    if (effectiveEventSlug) api.getEvent(effectiveEventSlug).then(setEvent).catch(() => {});
  }, [effectiveEventSlug]);

  /**
   * TICK-060 — bumped by the delivery panel the moment the server reports `ticketReady`, so the
   * page re-reads the order instead of waiting out the 5s poll the customer is staring at.
   */
  const [reloadNonce, setReloadNonce] = useState(0);
  const requestReload = useCallback(() => setReloadNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    /*
     * Only a 404 means the order is not there.
     *
     * This used to be a bare `catch` that set `notFound` on anything at all, and the poll below
     * fires every five seconds against a lookup limited to 30 requests a minute — so one rate-limit
     * answer, one dropped connection or one slow response replaced a live order with "ไม่พบคำสั่ง
     * ซื้อ" and stopped the poll, permanently, with no way back but a manual reload. It landed on
     * the worst possible screen: the one a customer is watching just after they have paid and just
     * after they have bound a delivery channel. Their ticket was never in danger; the page simply
     * told them it did not exist.
     *
     * The same conflation was already fixed a few lines below for the delivery request, where
     * "loading" and "failed" both used to render as "legacy". This is that bug's twin in the order
     * load itself.
     *
     * Anything that is not a 404 is treated as temporary: keep whatever is on screen and try again,
     * backing off so a page left open cannot be what keeps the limiter closed.
     */
    let failures = 0;
    const load = async () => {
      try {
        if (token) {
          const linked = await ticketLinkApi.getOrder(token);
          if (cancelled) return;
          failures = 0;
          setLinkedEventSlug(linked.eventSlug);
          setOrder(linked.order);
          return;
        }
        const o = await api.getOrder(orderKey, eventSlug);
        if (cancelled) return;
        failures = 0;
        setOrder(o);
        if (o.status === 'awaiting_verification' || o.status === 'pending_payment') {
          timer = setTimeout(load, 5000);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          return;
        }
        failures += 1;
        timer = setTimeout(load, Math.min(5000 * 2 ** (failures - 1), 60_000));
      }
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [orderKey, eventSlug, token, reloadNonce]);

  /*
   * TICK-062 — live channel/verification state. Deliberately no longer the source of the *contract*.
   *
   * This request can be slow, can fail, and 404s outright when `DELIVERY_SELECTION_V2` is off. The
   * page used to read the contract version out of it and fall back to 1 in all three cases, so
   * "loading" and "failed" both rendered as "legacy" — a v2 customer was told their ticket had gone
   * to the email and phone number they typed at checkout, which is a place it was never sent, and on
   * a failed request they were told that permanently. The contract now comes from the order itself
   * (`order.deliveryContractVersion`), which is stamped at creation and arrives in the response that
   * already decided whether there is a ticket at all.
   *
   * What is left here is what genuinely needs a second request: whether the ticket has actually been
   * sent yet. That has three states and not two, so it is modelled as three.
   */
  type DeliveryLoad =
    | { phase: 'loading' }
    | { phase: 'ready'; state: PublicDeliveryStateDto }
    | { phase: 'failed' };
  const [deliveryLoad, setDeliveryLoad] = useState<DeliveryLoad>({ phase: 'loading' });
  useEffect(() => {
    let cancelled = false;
    if (!order) return undefined;
    setDeliveryLoad({ phase: 'loading' });
    deliveryApi.getPublicState(order.code, effectiveEventSlug || undefined)
      .then((state) => { if (!cancelled) setDeliveryLoad({ phase: 'ready', state }); })
      .catch(() => { if (!cancelled) setDeliveryLoad({ phase: 'failed' }); });
    return () => { cancelled = true; };
  }, [order?.code, effectiveEventSlug, reloadNonce]);

  /*
   * TICK-090 — the ticket, for the events that show it here instead of sending it.
   *
   * Gated on `instantTicket` rather than attempted-and-caught: under the verified-channel journey
   * the endpoint answers 409 by design, and a page that fired it on every load would be manufacturing
   * a handled error on the majority of orders.
   *
   * The QR is merged into the order already on screen rather than replacing it. The two responses
   * describe the same order and only one of them carries `qrPayload`, so a replace would flicker the
   * whole card for the sake of one field.
   */
  useEffect(() => {
    let cancelled = false;
    if (deliveryLoad.phase !== 'ready') return undefined;
    const { state } = deliveryLoad;
    if (!state.instantTicket || !state.ticketReady) return undefined;
    if (!order || order.qrPayload) return undefined;
    const capability = readManagementToken(order.code);
    // No capability on this device means this is not the browser that bought the ticket. That is not
    // an error — it is the case `/myticket` exists for.
    if (!capability) return undefined;
    deliveryApi.getInstantTicket(order.code, capability, effectiveEventSlug || undefined)
      .then((res) => { if (!cancelled) setOrder((prev) => (prev ? { ...prev, ...res.order } : res.order)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [deliveryLoad, order?.code, order?.qrPayload, effectiveEventSlug]);

  /**
   * The contract is authoritative from the order and is never unknown once the order has loaded —
   * there is no `order` at all before then, and the page renders nothing. So there is no window in
   * which legacy copy can be shown to a v2 customer, or the reverse.
   */
  const isV2 = (order?.deliveryContractVersion ?? 1) >= 2;

  /**
   * Whether the ticket has been sent, for a v2 order — three answers, not two.
   *
   * `unknown` is the one that used to be missing. A v2 order whose delivery state has not arrived
   * has not been shown to be waiting on verification, and telling the customer to "choose and verify
   * a channel below" when they may have already done exactly that is its own wrong answer. Neutral
   * copy holds the space until the request settles either way.
   */
  const ticketDelivery: 'sent' | 'pending_channel' | 'unknown' = !isV2
    ? 'sent'
    : deliveryLoad.phase !== 'ready'
      ? 'unknown'
      : deliveryLoad.state.ticketReady ? 'sent' : 'pending_channel';

  useEffect(() => {
    if (order?.qrPayload) {
      QRCode.toDataURL(order.qrPayload, { margin: 0, width: 336, color: { dark: '#171717', light: '#fafafa' } })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(''));
    }
  }, [order?.qrPayload]);

  if (notFound) {
    return (
      <div className="page success-wrap" style={{ paddingTop: 120 }}>
        <PreviewBanner eventSlug={effectiveEventSlug} />
        <h1>ไม่พบคำสั่งซื้อ</h1>
        <p>ตรวจสอบลิงก์หรือรหัสคำสั่งซื้ออีกครั้ง</p>
        <button className="btn-ghost" onClick={() => navigate(prefix || '/')}>กลับหน้าแรก</button>
      </div>
    );
  }
  if (!order) return <div className="page" />;

  const eventFacts = (event?.facts ?? []).filter((fact) => fact.k.trim() || fact.v.trim());

  const body = (() => {
    switch (order.status) {
      case 'paid':
        return (
          <div className="verified-ticket-experience">
            <article className="verified-ticket-card" aria-label={`บัตร ${event?.name ?? ''} ${order.code}`}>
              <div className="verified-ticket-status-row">
                <span className="verified-ticket-status">✓ PAYMENT CONFIRMED</span>
                <span className="verified-ticket-order">ORDER #{order.code}</span>
              </div>
              <h1>{event?.name ?? 'บัตรของคุณพร้อมแล้ว'}</h1>
              {event?.tagline && <div className="verified-ticket-tagline">{event.tagline}</div>}
              <dl className="verified-ticket-facts verified-ticket-facts--summary">
                {eventFacts.slice(0, 2).map((fact, index) => (
                  <div key={`${fact.k}-${index}`}>
                    <dt>{fact.k}</dt>
                    <dd>{fact.v}</dd>
                  </div>
                ))}
                <div><dt>TICKETS / จำนวนบัตร</dt><dd>{order.qty}</dd></div>
                <div><dt>ENTRY / เข้างานได้</dt><dd>{order.entries} คน</dd></div>
              </dl>
              <div className="verified-ticket-tear" aria-hidden="true" />
              {/*
                TICK-025 (Gate G2): the QR only exists on this page when the server sent one, and
                it only does that for the ~72-bit ticket-link token — not for this ~25-bit order
                code. Branching on `order.qrPayload` rather than on the route keeps the client from
                ever disagreeing with the server about which surface is allowed to show it.
              */}
              <div className="verified-ticket-qr-block">
                {!order.qrPayload ? (
                  <div className="verified-ticket-qr-sent" data-testid="ticket-delivery-copy" data-delivery-state={isV2 ? ticketDelivery : 'legacy'}>
                    <strong>ชำระเงินสำเร็จแล้ว ✓</strong>
                    <span>
                      {/*
                        Four different truths, and telling a customer the wrong one sends them
                        looking in a place their ticket was never sent to. `unknown` is a state in
                        its own right: while the delivery request is in flight or has failed, this
                        says only what is certainly true — the money is in — and claims nothing
                        about where the ticket went.
                      */}
                      {!isV2 ? (
                        <>
                          บัตรพร้อม QR เข้างานถูกส่งไปที่อีเมลและ SMS ที่คุณกรอกไว้ตอนสั่งซื้อ — เปิดจากลิงก์นั้นเพื่อใช้เข้างาน<br />
                          ถ้ายังไม่ได้รับ ตรวจกล่องจดหมาย (รวมถึง Junk/Spam) อีกครั้ง หรือติดต่อทีมงานพร้อมแจ้งรหัสด้านล่าง
                        </>
                      ) : ticketDelivery === 'sent' ? (
                        <>
                          บัตรพร้อม QR เข้างานถูกส่งไปที่ช่องทางรับตั๋วที่คุณยืนยันไว้ — เปิดจากลิงก์นั้นเพื่อใช้เข้างาน<br />
                          ถ้ายังไม่ได้รับ ตรวจช่องทางที่เลือกไว้อีกครั้ง หรือติดต่อทีมงานพร้อมแจ้งรหัสด้านล่าง
                        </>
                      ) : ticketDelivery === 'pending_channel' ? (
                        <>
                          บัตรยัง<strong>ไม่ถูกส่ง</strong> เพราะยังไม่ได้ยืนยันช่องทางรับตั๋ว<br />
                          เลือกและยืนยันช่องทางด้านล่าง แล้วระบบจะส่งบัตรให้ทันที
                        </>
                      ) : (
                        <span aria-live="polite">กำลังตรวจสอบสถานะการส่งบัตร…</span>
                      )}
                    </span>
                  </div>
                ) : qrDataUrl ? (
                  <div className="verified-ticket-qr">
                    <img src={qrDataUrl} alt={`QR สำหรับเช็กอิน ${order.code}`} width={180} height={180} />
                  </div>
                ) : <div className="verified-ticket-qr-loading" aria-live="polite">กำลังสร้าง QR…</div>}
                <div className="verified-ticket-code">{order.code}</div>
                {order.qrPayload && (
                  <div className="verified-ticket-help">
                    แสดง QR นี้ที่ประตูทางเข้า · ใช้ได้ครั้งเดียว<br />
                    ไม่ต้องพิมพ์ — เปิดจากมือถือได้เลย
                  </div>
                )}
              </div>
            </article>

            {/*
              Screen D, paid-first ordering: the money is in and the ticket is not, because no
              channel has been verified yet. The panel is the way out of that state, so it belongs
              directly under the card rather than at the bottom of the page.
            */}
            {!order.qrPayload && isV2 && (
              <DeliveryChannelPanel
                orderCode={order.code}
                eventSlug={effectiveEventSlug || undefined}
                orderStatus={order.status}
                onTicketReady={requestReload}
              />
            )}

            <section className="verified-ticket-panel" aria-labelledby="verified-ticket-lines">
              <h2 id="verified-ticket-lines">รายการบัตร / TICKETS</h2>
              <div className="verified-ticket-lines">
                {order.lines.map((line) => {
                  const customText = formatCustomFieldValuesWithLabels(
                    line.customFieldValues,
                    event?.tickets.find((t) => t.id === line.ticketId)?.customFields ?? [],
                  );
                  return (
                    <div className="verified-ticket-line" key={line.ticketId}>
                      <div>
                        <strong>{line.label}</strong>
                        <span>{fmt(line.unitPrice)} × {line.qty}</span>
                        {customText && <span className="verified-ticket-line-custom">{customText}</span>}
                      </div>
                      <b>{fmt(line.lineTotal)}</b>
                    </div>
                  );
                })}
                <div className="verified-ticket-total"><span>TOTAL PAID · โอนผ่านธนาคาร</span><strong>{fmt(order.total)}</strong></div>
              </div>
            </section>

            {eventFacts.length > 0 && (
              <section className="verified-ticket-panel" aria-labelledby="verified-ticket-good-to-know">
                <h2 id="verified-ticket-good-to-know">ข้อมูลสำคัญ / GOOD TO KNOW</h2>
                <dl className="verified-ticket-facts verified-ticket-facts--details">
                  {eventFacts.map((fact, index) => (
                    <div key={`${fact.k}-${index}`}><dt>{fact.k}</dt><dd>{fact.v}</dd></div>
                  ))}
                </dl>
              </section>
            )}

            {order.checkedInAt && (
              <div className="verified-ticket-checked-in" role="status">
                เช็กอินแล้วเมื่อ {new Date(order.checkedInAt).toLocaleString('th-TH')}
              </div>
            )}
            <div className="verified-ticket-actions">
              {qrDataUrl && (
                <button className="btn-primary" disabled={savingCard} onClick={saveTicketCard}>
                  {savingCard ? 'กำลังสร้างรูป…' : 'บันทึกรูป QR ลงเครื่อง'}
                </button>
              )}
              {/* On the order-code path this URL is no longer the ticket, so don't call it one. */}
              <button className="btn-ghost" onClick={copyLink}>
                {linkCopied ? 'คัดลอกลิงก์แล้ว ✓' : order.qrPayload ? 'คัดลอกลิงก์บัตร' : 'คัดลอกลิงก์หน้านี้'}
              </button>
              <span className="sr-only" role="status" aria-live="polite">{linkCopied ? 'คัดลอกลิงก์บัตรแล้ว' : ''}</span>
            </div>
            <footer className="verified-ticket-footer">
              {/*
                Two LINE links, and the page has to keep them apart: the organizer's is news from the
                people running the event, JamTick's is support for the order. The organizer's comes
                first because it is about the event the customer just bought into; both sit below
                every control that does something to the order.
              */}
              <OrganizerOaInvite oa={event?.organizerOa ?? null} />
              {/* QA (2026-08-27): swapped the raw support-email/copyright text for a direct path to help. */}
              <a className="btn-ghost" href={JAMTICK_LINE_ADD_FRIEND_URL} target="_blank" rel="noreferrer">
                เพิ่มเพื่อน LINE JamTick หากต้องการความช่วยเหลือ
              </a>
            </footer>
          </div>
        );
      case 'awaiting_verification':
        return (
          <>
            <div className="mono-label pulse" style={{ letterSpacing: '0.2em', marginBottom: 14 }}>VERIFYING PAYMENT</div>
            <h1>รอตรวจสอบสลิป</h1>
            <p data-testid="awaiting-verification-copy" data-delivery-contract={isV2 ? '2' : '1'}>
              ทีมงานกำลังตรวจสอบยอดโอน (ภายใน 24 ชม.)<br />
              {isV2
                ? 'ต้องครบทั้งสองอย่าง: อนุมัติการชำระเงิน และยืนยันช่องทางรับตั๋ว — หน้านี้อัปเดตอัตโนมัติ'
                : 'เมื่ออนุมัติแล้ว บัตร QR จะถูกส่งไปที่อีเมลและ SMS ที่คุณกรอกไว้ — หน้านี้อัปเดตอัตโนมัติ'}
            </p>
            {/*
              TICK-060 Screen A — a decorative lock, not a QR. It carries no encoded payload of any
              kind, and its alt text says so: a screen-reader user must not be told there is a code
              here that could get them through the gate.
            */}
            <div className="qr-placeholder" role="img" aria-label="ตัวอย่างพื้นที่แสดง QR — ยังไม่มี QR เข้างานในขณะนี้">
              <span className="qr-placeholder-lock" aria-hidden="true">🔒</span>
              <span className="qr-placeholder-text">
                {isV2
                  ? <>QR เข้างานจะแสดงหลังยืนยันการชำระเงิน<br />และยืนยันช่องทางรับตั๋ว</>
                  : <>QR เข้างานจะแสดงหลังยืนยันการชำระเงิน</>}
              </span>
            </div>
            <div className="order-code">{order.code}</div>
            <div className="order-meta">{order.qty} ใบ · เข้างานได้ {order.entries} คน · ยอดโอน {fmt(order.total)}</div>
            {/*
              This panel must stay reachable while the payment is still being checked, and the two
              verifications must stay independent (TICK-060).

              It was briefly removed here on QA feedback that customers were tapping a channel too
              early. That serialised the two steps, and `sendTicketNotifications` refuses to send
              anything to an order with no verified primary — so a customer who closed the page
              during the wait was never asked for a channel, and their ticket silently never went
              out. This page is the only thing that tells them what is missing, which is exactly why
              it cannot be hidden during the window they are most likely to be looking at it.
            */}
            {isV2 && (
              <DeliveryChannelPanel
                orderCode={order.code}
                eventSlug={effectiveEventSlug || undefined}
                orderStatus={order.status}
                onTicketReady={requestReload}
              />
            )}
            <div className="order-meta" style={{ marginTop: 18, maxWidth: 420 }}>
              กรุณาเก็บหลักฐานการโอนเงินไว้ให้ดี และบันทึกลิงก์หน้านี้ไว้เพื่อกลับมาเช็คสถานะบัตรภายหลัง
            </div>
            <button className="btn-ghost" style={{ marginTop: 10 }} onClick={copyLink}>
              {linkCopied ? 'คัดลอกลิงก์แล้ว ✓' : 'คัดลอกลิงก์หน้านี้'}
            </button>
            {/*
              The slip is still being checked, so this stays last on the page and stays quiet. The
              statuses that are not here are the deliberate part: a customer whose payment was
              rejected, or whose order expired, has a problem to solve, and an invitation is the
              wrong thing to put in front of them.
            */}
            <OrganizerOaInvite oa={event?.organizerOa ?? null} />
          </>
        );
      case 'rejected':
        return (
          <>
            <div className="mono-label" style={{ letterSpacing: '0.2em', marginBottom: 14 }}>PAYMENT REJECTED</div>
            <h1>สลิปไม่ผ่านการตรวจสอบ</h1>
            <p>
              ยอดโอนไม่ตรงหรือสลิปไม่ถูกต้อง — บัตรถูกปล่อยกลับเข้าระบบแล้ว<br />
              หากโอนเงินแล้วจริง กรุณาติดต่อ TICKETS@MOONFIELD.LIVE พร้อมรหัส {order.code}
            </p>
            <div className="order-code">{order.code}</div>
          </>
        );
      case 'pending_payment':
        return (
          <>
            <h1>คำสั่งซื้อยังไม่เสร็จสมบูรณ์</h1>
            <p>ยังไม่ได้แนบสลิป — กลับไปหน้าชำระเงินเพื่อทำรายการต่อ</p>
            <button className="btn-primary" onClick={() => navigate(`${prefix}/checkout/${order.id}`)}>
              ไปหน้าชำระเงิน
            </button>
          </>
        );
      default:
        return (
          <>
            <h1>{order.status === 'expired' ? 'คำสั่งซื้อหมดอายุ' : 'คำสั่งซื้อถูกยกเลิก'}</h1>
            <p>บัตรถูกปล่อยกลับเข้าระบบแล้ว — เลือกบัตรใหม่ได้จากหน้าแรก</p>
          </>
        );
    }
  })();

  return (
    <div className={`page order-status-page ${order.status === 'paid' ? 'order-status-page--verified' : ''}`}>
      <PreviewBanner eventSlug={effectiveEventSlug} force={order.preview} />
      <header className={`site-header shell ${order.status === 'paid' ? 'verified-ticket-page-header' : ''}`}>
        <div className="logo">{order.status === 'paid' ? 'JAMTICK' : event?.name ?? ''}</div>
        {order.status !== 'paid' && <span className="mono-label" style={{ letterSpacing: '0.12em' }}>ORDER {order.code}</span>}
      </header>
      <main className={order.status === 'paid' ? 'verified-ticket-wrap' : 'success-wrap'}>
        {body}
        <div className={order.status === 'paid' ? 'verified-ticket-back' : undefined} style={order.status === 'paid' ? undefined : { marginTop: 44, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn-ghost" onClick={() => navigate(prefix || '/')}>กลับหน้าแรก</button>
        </div>
      </main>
    </div>
  );
}
